import { Breakpoint, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { Feature } from 'geojson'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import { SubjectFeature } from '../../../common/subjectTypes'
import {
  resetMapHoveredId,
  setMapHoveredId,
  toggleMapPopupData,
} from '../../../redux/mapSlice'
import { nestedStringSorter } from '../../utils/generalTableHelpers'
import { tablePagination } from '../../utils/layoutConst'
import { subjectDetailPath } from '../../utils/paths'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { SubjectSearchResultTableAction } from '../SubjectSearchResultTableAction/SubjectSearchResultTableAction'
import { SubjectSearchResultTableAddressCell } from '../SubjectSearchResultTableAddressCell/SubjectSearchResultTableAddressCell'
import { SubjectSearchResultTableSummary } from '../SubjectSearchResultTableSummary/SubjectSearchResultTableSummary'

export interface SubjectSearchResultTableProps {
  data: SubjectFeature[]
}

const getPartsColSpan = (record: SubjectFeature) => ({
  colSpan: record.properties.ulice ? 1 : 0,
})
const getAddressColSpan = (record: SubjectFeature) => ({
  colSpan: record.properties.ulice ? 1 : 4,
})

const rowKey = (record: SubjectFeature) => record.properties.id
const tableId = 'subjectSearchResult-table'

export const SubjectSearchResultTable: FunctionComponent<SubjectSearchResultTableProps> =
  props => {
    const dispatch = useDispatch()
    const { nestedSearchFilter } = useColumnFilters<SubjectFeature>()

    const onRow = useCallback(
      (record: SubjectFeature) => ({
        onMouseEnter: () => dispatch(setMapHoveredId(record.properties.id)),
        onMouseLeave: () => dispatch(resetMapHoveredId()),
        className: record.properties.historicky ? 'text-gray-400' : '',
      }),
      [dispatch]
    )

    const handleClick = useCallback(
      (record: SubjectFeature) => ({
        onClick: () => dispatch(toggleMapPopupData(record as Feature)),
      }),
      [dispatch]
    )

    const columns = useMemo<ColumnsType<SubjectFeature>>(
      () => [
        {
          title: 'ID',
          dataIndex: ['properties', 'id'],
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Subjekt',
          className: 'xl:w-72 2xl:w-1/2',
          dataIndex: ['properties', 'nazev'],
          sorter: nestedStringSorter('properties', 'nazev'),
          ...nestedSearchFilter(
            'properties',
            'nazev',
            'Vyhledat v názvu subjektu'
          ),
          render: (nazev, record) => (
            <Link
              title="Přejít na detail subjektu"
              to={subjectDetailPath(record.properties.id)}
            >
              {nazev}
            </Link>
          ),
        },
        {
          title: 'Ulice',
          dataIndex: ['properties', 'ulice'],
          onCell: getAddressColSpan,
          sorter: nestedStringSorter('properties', 'adresa'),
          ...nestedSearchFilter(
            'properties',
            'ulice',
            'Vyhledat v názvu ulice'
          ),
          render: (_, record) => (
            <SubjectSearchResultTableAddressCell record={record} />
          ),
        },
        {
          title: 'Obec',
          onCell: getPartsColSpan,
          dataIndex: ['properties', 'obec'],
          sorter: nestedStringSorter('properties', 'obec'),
          ...nestedSearchFilter('properties', 'obec', 'Vyhledat v názvu obce'),
        },
        {
          title: 'Část obce',
          dataIndex: ['properties', 'castObce'],
          onCell: getPartsColSpan,
          sorter: nestedStringSorter('properties', 'castObce'),
          ...nestedSearchFilter(
            'properties',
            'castObce',
            'Vyhledat v názvu části obce'
          ),
        },
        {
          title: 'PSČ',
          dataIndex: ['properties', 'psc'],
          className: 'w-20',
          onCell: getPartsColSpan,
          sorter: nestedStringSorter('properties', 'psc'),
          ...nestedSearchFilter('properties', 'psc', 'Vyhledat v PSČ'),
        },
        {
          dataIndex: ['properties', 'id'],
          key: 'action',
          className:
            '!py-0 text-right cursor-pointer text-primary hover:text-primaryHover',
          onCell: handleClick,
          render: (_, record) => (
            <SubjectSearchResultTableAction record={record} />
          ),
        },
      ],
      [handleClick, nestedSearchFilter]
    )

    const summary = useCallback(
      () =>
        props.data.length ? (
          <SubjectSearchResultTableSummary totalCount={props.data.length} />
        ) : undefined,
      [props.data?.length]
    )

    return (
      <Table
        id={tableId}
        size="small"
        rowKey={rowKey}
        onRow={onRow}
        summary={summary}
        dataSource={props.data || []}
        columns={columns}
        pagination={tablePagination}
      />
    )
  }
